import React, { useEffect, useRef, useState } from "react";
import { Button, Card, ConfigProvider, Switch, Typography, Tag, Space, Tooltip, Divider, Alert, Row, Col, Grid } from 'antd';
import { t } from "i18next";
import ApiService from "../../../core/service/ApiService";
import DataGrid from "../../../core/components/data-grid/DataGrid";
import { useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import Helper from "../../../core/Helper";
import _ from "lodash";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import { useModal } from "../../../core/context/ModalContext";
import { useParams } from "react-router-dom";
import { PatientSummaryRow } from "./PatientSummaryRow";
import { PatientSummaryCard } from "./PatientSummaryCard";
import AppointmentCard from "../AppointmentCard";
import AppointmentPreviewApp from "../preview/AppointmentPreviewApp";
import { PlusOutlined, WalletOutlined, FilterOutlined, CheckCircleOutlined, MedicineBoxOutlined } from '@ant-design/icons';

const { Text } = Typography;

// Hasta randevuları grid'ine özel tema (genel temadaki amber yerine): anahtarlar marka slate'i,
// seçili satır slate'in açık tonu — koyu metin, kırmızı/yeşil bakiye okunur kalır; aktif-seçili olmayan
// satırın zemininden (#E8EDF2, index.css) bir ton koyu.
const gridTheme = {
    components: {
        Switch: {
            colorPrimary: '#34495E',
            colorPrimaryHover: '#2C3E50',
        },
        Table: {
            rowSelectedBg: '#D5DDE6',
            rowSelectedHoverBg: '#CBD4DE',
        },
    },
}

// Bu satır sayısının üstünde sanallaştırma devreye girer
const VIRTUAL_ROW_THRESHOLD = 50;

export const AppointmentPatientDataGrid = ({ filter, tableUpdate, onRows, rows, openCalendar, onPayment, activeAppointmentId, onActivate }) => {
    const params = useParams();
    const prevRows = useRef([])
    const isPageLoad = useRef(false)
    const [selectedTotal, setSelectedTotal] = useState(0)
    const { hasPermission, formatPrice, user } = useUser();
    const { openModal } = useModal();
    const { setData, setPaginationResult, queryModel, setDataGridProps, selectedRows, onFilterPageSort, setSelectedRows, resultModel, refreshDataGrid } = useDataGrid();
    // onRow bir kez (props effect'inde) kuruluyor; tıklama anındaki güncel seçimi ref'ten oku
    const selectedRowsRef = useRef(selectedRows)
    selectedRowsRef.current = selectedRows

    const openAppointmentPreview = (row) => {
        openModal(
            t('Appointment Preview'),
            <AppointmentPreviewApp
                appointmentId={row.appointmentId}
                onDelete={() => refreshDataGrid()}
            />,
            () => refreshDataGrid()
        );
    };
    const screens = Grid.useBreakpoint();
    const isMobileLayout = !screens.md;

    // --- DATA LOADING ---
    const load = async () => {
        const input = {
            ...queryModel,
            filter: {
                ...queryModel.filter,
                startDate: Helper.Date.beginOfDay(queryModel.filter.startDate),
                endDate: Helper.Date.endOfDay(queryModel.filter.endDate),
            }
        }

        input.paginationQuery = {
            pageNumber: 1,
            pageSize: 10000, // Client-side pagination gibi davranıyor
        }

        if (!input.filter.patientId) return

        const res = await ApiService.Appointment.GetAppointmentDataGrid(input);
        if (res.success) {
            setData(res.data)
            setPaginationResult(res.pagination)
        }
    }

    // --- SEÇİLENLERİN TOPLAMINI HESAPLA ---
    const getTotalBalance = async (selectedIds) => {
        if (selectedIds.length === 0) {
            setSelectedTotal(0);
            return;
        }
        const res = await ApiService.Appointment.GetTotalPatientBalance(params.patientId, selectedIds);
        if (res.success) {
            setSelectedTotal(res.data.selectedTotal)
        }
    }

    // --- KOLON TANIMLARI ---
    // Kart içinde dar alanda yatay kaydırma olmasın diye: Id, Tarih (saat altında),
    // birim/tür/kurum tek hücrede, üç tutar Bakiye hücresinde toplanır.
    // Sade tutuldu: renk yalnızca durum noktası ve bakiyede; ikincil bilgiler gri.
    const getColumns = () => {
        return [
            {
                key: 'appointmentId',
                dataIndex: 'appointmentId',
                title: t('Id'),
                width: 90,
                render: (text, row) => {
                    const canPreview = hasPermission(PERMISSIONS.APPOINTMENT_VIEW_OTHER_USER) || row.responsibleUserId === user?.id;
                    // Randevu no → önizleme (eski "+" butonunun işlevi)
                    return canPreview ? (
                        <Tooltip title={t('Appointment Preview')}>
                            <Button size="small" style={{ minWidth: 64 }} onClick={() => openAppointmentPreview(row)}>#{text}</Button>
                        </Tooltip>
                    ) : (
                        <Button size="small" style={{ minWidth: 64 }} disabled>#{text}</Button>
                    );
                }
            },
            {
                key: 'startDate',
                dataIndex: 'startDate',
                sorter: true,
                title: t('Date'),
                width: 110,
                render: (text) => {
                    const [date, time] = Helper.Date.dateFormat(text).split(' ');
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{date}</span>
                            <span className={'appointment-muted'} style={{ fontSize: 11, color: '#888' }}>{time}</span>
                        </div>
                    );
                }
            },
            {
                key: 'details',
                title: t('Appointment'),
                width: 220,
                render: (_, row) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                        <Text ellipsis={{ tooltip: [row.unitName, row.appointmentTypeName].filter(Boolean).join(' · ') }}>
                            <span style={{ fontWeight: 600 }}>{row.unitName}</span>
                            {row.appointmentTypeName && <span className={'appointment-muted'} style={{ color: '#888' }}> · {row.appointmentTypeName}</span>}
                        </Text>
                        {row.institutionName && (
                            <Text type="secondary" className={'appointment-muted'} style={{ fontSize: 11 }} ellipsis={{ tooltip: row.institutionName }}>
                                {row.institutionName}
                            </Text>
                        )}
                    </div>
                )
            },
            {
                key: 'stateName',
                dataIndex: 'stateName',
                columnName: 'state.name',
                sorter: true,
                title: t('State'),
                width: 140,
                render: (text, row) => (
                    <Tooltip title={t(text)}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
                            <span style={{
                                width: 8,
                                height: 8,
                                flexShrink: 0,
                                borderRadius: '50%',
                                background: row.stateColor,
                                boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                            }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {t(text)}
                            </span>
                        </span>
                    </Tooltip>
                )
            },
            {
                key: 'balance',
                dataIndex: 'balance',
                title: t('Outstanding'),
                sorter: true,
                align: 'right',
                width: 170,
                render: (text, row) => {
                    if (row.totalPrice === -1) return '-';
                    const currency = { currency: row?.currency || 'TRY' };
                    const hasDebt = row.balance > 0;
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                            <Text strong style={{ color: hasDebt ? '#cf1322' : '#389e0d', whiteSpace: 'nowrap' }}>
                                {formatPrice(row.balance, currency)}
                            </Text>
                            <Tooltip title={`${t('Paid Amount')} / ${t('Amount')}`}>
                                <span className={'appointment-muted'} style={{ fontSize: 11, color: '#999', whiteSpace: 'nowrap' }}>
                                    {formatPrice(row.totalPaid, currency)} / {formatPrice(row.totalPrice, currency)}
                                </span>
                            </Tooltip>
                        </div>
                    );
                }
            },
        ]
    }

    // --- EFFECTS ---
    useEffect(() => { load() }, [queryModel.updateKey])
    useEffect(() => { if (tableUpdate) refreshDataGrid() }, [tableUpdate]);
    useEffect(() => { onFilterPageSort({ filter: { ...filter } }) }, [filter]);

    // --- MOBİL KART RENDER ---
    // Hasta bağlamında hasta bilgisi gizli; geri kalan (status + önizleme) aynı.
    const renderCard = (row) => {
        const canViewOther = hasPermission(PERMISSIONS.APPOINTMENT_VIEW_OTHER_USER);
        const canPreview = canViewOther || row.responsibleUserId === user?.id;
        return (
            <AppointmentCard
                row={row}
                formatPrice={formatPrice}
                showPatient={false}
                showPreview={true}
                canPreview={canPreview}
                onPreview={openAppointmentPreview}
            />
        );
    };

    // --- SATIR TIKLAMA ---
    // Satıra tık: randevu aktif olur (detay kartı açılır) ve seçim yalnızca o satır olur.
    // Ctrl/Cmd + tık: aktif randevu değişmeden satır seçime eklenir/çıkarılır (checkbox ile aynı).
    // Satır içindeki link/buton/checkbox kendi işini yapar, satır tıklaması tetiklenmez.
    const handleRowClick = (event, row) => {
        if (event.target.closest('a, button, input, label, .ant-checkbox-wrapper, .ant-table-selection-column')) return;
        const current = selectedRowsRef.current;
        const isSelected = current.some(r => r.appointmentId === row.appointmentId);
        if (event.ctrlKey || event.metaKey) {
            setSelectedRows(isSelected
                ? current.filter(r => r.appointmentId !== row.appointmentId)
                : [...current, row]);
            return;
        }
        onActivate && onActivate(row.appointmentId);
        if (!(isSelected && current.length === 1)) setSelectedRows([row]);
    };

    // Tek hastanın randevuları çoğunlukla bir avuç satır: sanallaştırma sabit yükseklikli
    // scroll alanı ister ve 3 satırda bile boş dikey kaydırma çubuğu çıkarır. Yalnızca
    // gerçekten uzun listelerde aç.
    const isVirtual = (resultModel.data?.length || 0) > VIRTUAL_ROW_THRESHOLD;

    useEffect(() => {
        let columns = getColumns()
        let props = {
            rowKey: 'appointmentId',
            enableRowSelection: onRows, // Seçim aktif
            expandedRowRender: false,
            columns: columns,
            rowClassName: (row) => [
                row.isDeleted ? 'deleted-row' : '',
                'appointment-row-clickable',
                row.appointmentId === activeAppointmentId ? 'appointment-row-active' : '',
            ].filter(Boolean).join(' '),
            onRow: (row) => ({ onClick: (event) => handleRowClick(event, row) }),
            cardRender: renderCard,
            cardSummary: () => <PatientSummaryCard />,
            virtual: isVirtual,
            virtualRowHeight: 52, // iki satırlık hücreler (size: 'small')
            antdTableProps: {
                summary: () => <PatientSummaryRow colSpan={onRows ? 6 : 5} />,
                // Boş scroll: DataGrid'in varsayılan x:1280 / y:ekran yüksekliği kaydırmasını kapatır;
                // sütunlar kart genişliğine sığıyor.
                scroll: {},
                size: 'small' // Daha kompakt liste
            }
        }
        props.antdTableProps.pagination = false
        setDataGridProps(props)
    }, [isVirtual, activeAppointmentId])

    useEffect(() => {
        onRows && onRows(selectedRows)
        if (selectedRows.length > 0) {
            const selectedIds = selectedRows.map(row => row.appointmentId);
            getTotalBalance(selectedIds);
        } else {
            setSelectedTotal(0);
        }
    }, [selectedRows]);

    useEffect(() => {
        if (!_.isEqual(prevRows.current, rows)) {
            prevRows.current = rows
            setSelectedRows(rows)
        }
    }, [rows]);

    useEffect(() => {
        if (!isPageLoad.current && params.appointmentId) {
            isPageLoad.current = true
            setSelectedRows([{ appointmentId: parseInt(params.appointmentId) }])
        }
    }, [resultModel.data])

    // Tümünü Göster + Sadece Borç: masaüstünde araç çubuğunda, mobilde yenile düğmesinin yanında (toolbarExtra)
    const showAllSwitch = (
        <Space size={6}>
            <Switch
                size="small"
                checked={!(queryModel.filter.stateGroupIdList?.length === 1)}
                onChange={(checked) => {
                    onFilterPageSort({
                        filter: { ...queryModel.filter, stateGroupIdList: checked ? [] : [1] }
                    })
                }}
            />
            <Text style={{ fontSize: 13 }}>{t('Show All')}</Text>
        </Space>
    );

    const debtOnlySwitch = (
        <Space size={6}>
            <Switch
                size="small"
                checked={queryModel.filter.hideZeroBalance}
                onChange={(checked) => {
                    onFilterPageSort({
                        filter: { ...queryModel.filter, hideZeroBalance: checked }
                    })
                }}
            />
            <Text style={{ fontSize: 13 }}>{t('Debt Only')}</Text>
        </Space>
    );

    const renderMobileSwitches = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, whiteSpace: 'nowrap' }}>
            {showAllSwitch}
            {debtOnlySwitch}
        </div>
    );

    // --- RENDER TOOLBAR ---
    const renderToolbar = () => {
        const selectedBlock = selectedRows.length > 0 && (
            <>
                {/* Sade tutuldu: toplam gövde metni renginde, ödeme butonu nötr; vurgu tek birincil butonda (Yeni Randevu) */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, whiteSpace: 'nowrap' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Total Selected')}:</Text>
                    <Text strong>
                        {formatPrice(selectedTotal)}
                    </Text>
                </div>

                {hasPermission(PERMISSIONS.PAYMENT_CREATE_EDIT) && (
                    <Button
                        icon={<WalletOutlined />}
                        size={isMobileLayout ? 'middle' : 'small'}
                        onClick={onPayment}
                        block={isMobileLayout}
                    >
                        {t('Receive Payment')}
                    </Button>
                )}
            </>
        );

        const newAppointmentButton = hasPermission(PERMISSIONS.APPOINTMENT_CREATE_EDIT) && (
            <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCalendar}
                size={isMobileLayout ? 'middle' : 'small'}
                block={isMobileLayout}
            >
                {t('New Appointment')}
            </Button>
        );

        // MOBİL: Dikey stack
        if (isMobileLayout) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                    {/* Anahtarlar mobilde yenile düğmesinin yanında (toolbarExtra) — ayrı satır açmıyor */}
                    {/* Seçili toplam + Ödeme butonu (varsa) */}
                    {selectedRows.length > 0 && (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            background: '#fafafa',
                            padding: '8px 10px',
                            borderRadius: 4,
                            border: '1px solid #f0f0f0'
                        }}>
                            {selectedBlock}
                        </div>
                    )}

                    {/* Yeni randevu — her zaman alt */}
                    {newAppointmentButton}
                </div>
            );
        }

        // DESKTOP: Mevcut yatay layout
        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, flex: 1 }}>
                <Space size="small">
                    {showAllSwitch}
                    {debtOnlySwitch}
                </Space>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {selectedBlock}
                    {newAppointmentButton}
                </div>
            </div>
        );
    };

    return (
        <ConfigProvider theme={gridTheme}>
            <div>
                <Card
                    bordered={false}
                    style={{ backgroundColor: 'transparent', boxShadow: 'none' }} // İç içe kart görünümünü kaldırdım
                    bodyStyle={{ padding: 0 }}
                >
                    <DataGrid
                        name={'appointments'}
                        title={renderToolbar}
                        toolbarExtra={isMobileLayout ? renderMobileSwitches() : null}
                    />
                </Card>
            </div>
        </ConfigProvider>
    )
}